/**
 * Notification + background-fetch setup for the Android build.
 *
 * Two layers, intentionally redundant because Android background-fetch is
 * unreliable on its own:
 *
 *   1. A fixed daily local notification at 18:35 Berlin. Always fires (OS-
 *      level alarm), but says only the generic "Tagesumsätze sind da" —
 *      doesn't actually know whether the importer pulled anything.
 *
 *   2. A background-fetch task that pings GET /transactions/recent-summary
 *      with the highest created_at the device has seen. If the count is
 *      non-zero, it posts a richer notification ("N neue Umsätze"). This
 *      is best-effort — Doze + battery savers may starve the fetch — but
 *      when it fires, it tells the user something concrete.
 *
 * Why both: the local notification guarantees *some* signal at 18:35 so
 * the user has a reliable hook into checking the app. The fetch task
 * upgrades that signal to a specific message when it manages to run.
 *
 * State lives in localStorage:
 *   - notifications:last-seen-created-at — iso8601 of the newest tx the
 *     device knows about. Initialised on first scheduler run; updated
 *     after every successful fetch.
 */
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { BackgroundFetch } from "@transistorsoft/capacitor-background-fetch";
import { transactions as txApi } from "../api/client";

const DAILY_NOTIFICATION_ID = 1001;
const FRESH_BATCH_NOTIFICATION_ID = 1002;
const LAST_SEEN_KEY = "notifications:last-seen-created-at";

function readLastSeen(): string {
  try {
    const v = localStorage.getItem(LAST_SEEN_KEY);
    if (v) return v;
  } catch {}
  // Default: 24h ago, so the very first run only flags genuinely new txs
  // rather than dumping every historical row into a notification.
  return new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
}

function writeLastSeen(iso: string): void {
  try {
    localStorage.setItem(LAST_SEEN_KEY, iso);
  } catch {}
}

async function ensurePermission(): Promise<boolean> {
  const status = await LocalNotifications.checkPermissions();
  if (status.display === "granted") return true;
  const req = await LocalNotifications.requestPermissions();
  return req.display === "granted";
}

async function scheduleDailyAtBerlin1835(): Promise<void> {
  // Cancel any previous instance — we re-schedule on every app boot so
  // a code change to the message text takes effect on next launch.
  try {
    await LocalNotifications.cancel({ notifications: [{ id: DAILY_NOTIFICATION_ID }] });
  } catch {}

  await LocalNotifications.schedule({
    notifications: [
      {
        id: DAILY_NOTIFICATION_ID,
        title: "Heutige Umsätze",
        body: "Schau kurz rein — deine Tagesbuchungen sollten jetzt drin sein.",
        // Repeats daily at the given hour:minute (device local time).
        // Berlin is the canonical user; if they roam, device-local is the
        // friendlier choice anyway.
        schedule: {
          on: { hour: 18, minute: 35 },
          allowWhileIdle: true,
          repeats: true,
        },
        smallIcon: "ic_stat_logbuch",
        channelId: "daily-import",
      },
    ],
  });
}

async function ensureChannel(): Promise<void> {
  try {
    await LocalNotifications.createChannel({
      id: "daily-import",
      name: "Tägliche Umsätze",
      description: "Erinnert nach dem täglichen Bank-Pull.",
      importance: 3,
      visibility: 1,
    });
  } catch {}
}

async function postFreshBatch(count: number): Promise<void> {
  await LocalNotifications.schedule({
    notifications: [
      {
        id: FRESH_BATCH_NOTIFICATION_ID,
        title: count === 1 ? "1 neuer Umsatz" : `${count} neue Umsätze`,
        body: "Aus dem Bank-Import gerade reingekommen — tippen zum Öffnen.",
        schedule: { at: new Date(Date.now() + 100) },
        smallIcon: "ic_stat_logbuch",
        channelId: "daily-import",
      },
    ],
  });
}

async function runFetchCycle(): Promise<void> {
  const since = readLastSeen();
  try {
    const summary = await txApi.recentSummary(since);
    if (summary.count > 0) {
      await postFreshBatch(summary.count);
    }
    if (summary.latest_created_at) {
      writeLastSeen(summary.latest_created_at);
    } else if (summary.server_time) {
      // No new txs — bump the watermark forward so we don't keep
      // re-checking the same empty window every cycle.
      writeLastSeen(summary.server_time);
    }
  } catch {
    // Network / auth fail — leave watermark alone, retry next cycle.
  }
}

async function initBackgroundFetch(): Promise<void> {
  const status = await BackgroundFetch.configure(
    {
      // 15 min is the lowest the OS will respect. Real cadence is dictated
      // by Doze / Android job scheduler — could be anything from 15 min to
      // a few hours apart. That's OK: we just need at least one run after
      // 18:30 most days.
      minimumFetchInterval: 15,
      stopOnTerminate: false,
      startOnBoot: true,
    },
    async (taskId: string) => {
      await runFetchCycle();
      BackgroundFetch.finish(taskId);
    },
    async (taskId: string) => {
      // Timeout callback — must finish quickly.
      BackgroundFetch.finish(taskId);
    },
  );
  // Status: 0=restricted, 1=denied, 2=available
  if (status !== 2) {
    // Doze restrictions / user denied — local notification still fires
    // daily as the guaranteed fallback.
  }
}

let initialized = false;

export async function initNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform() || initialized) return;
  initialized = true;

  // Tap on any of our notifications ("Tagesumsätze sind da" / "N neue
  // Umsätze") lands the user on the Umsätze list — that's what the message
  // is about. Registered before the permission gate so a cold-start tap
  // (queued by the plugin until a listener exists) is never dropped.
  // App.tsx handles the event with react-router so no full reload happens.
  try {
    await LocalNotifications.addListener("localNotificationActionPerformed", () => {
      window.dispatchEvent(
        new CustomEvent("logbuch:navigate", { detail: "/transactions" }),
      );
    });
  } catch {}

  const granted = await ensurePermission();
  if (!granted) return;
  await ensureChannel();
  await scheduleDailyAtBerlin1835();
  await initBackgroundFetch();
}
